import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getGroupsBySection,
  createGroup,
  updateGroup,
  deleteGroup,
} from "../../../../../services/groupServices";

import {
  buildGroupPayload,
  filterGroups,
  getFallbackSectionId,
  getGroupsStats,
  isDemoMode,
  sortGroups,
} from "../utils/groupesUtils";

export default function useGroupes(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    type: null,
    group: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchGroupes = useCallback(async () => {
    if (!finalSectionId) {
      setGroupes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getGroupsBySection(finalSectionId);
      const safeData = Array.isArray(data) ? data : [];

      setGroupes(sortGroups(safeData));
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes :", error);
      toast.error("Erreur lors de la récupération des groupes.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchGroupes();
  }, [fetchGroupes]);

  const stats = useMemo(() => getGroupsStats(groupes), [groupes]);

  const rows = useMemo(() => {
    return filterGroups(groupes, searchTerm, statusFilter);
  }, [groupes, searchTerm, statusFilter]);

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const openCreateDrawer = () => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "create",
      group: null,
    });
  };

  const openEditDrawer = (group) => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "edit",
      group,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      group: null,
    });
  };

  const handleCreate = async (formData) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = buildGroupPayload(formData, finalSectionId);

      await createGroup(payload);

      toast.success("Groupe créé avec succès.");
      await fetchGroupes();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création du groupe :", error);
      toast.error("Erreur lors de la création du groupe.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (groupId, formData) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = {
        id: groupId,
        ...buildGroupPayload(formData, finalSectionId),
      };

      await updateGroup(payload);

      toast.success("Groupe modifié avec succès.");
      await fetchGroupes();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification du groupe :", error);
      toast.error("Erreur lors de la modification du groupe.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer ce groupe ?");

    if (!confirmed) return;

    try {
      await deleteGroup(groupId);

      setGroupes((prev) => prev.filter((group) => group.id !== groupId));

      toast.success("Groupe supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression du groupe :", error);
      toast.error("Erreur lors de la suppression du groupe.");
    }
  };

  return {
    finalSectionId,
    groupes,
    rows,
    stats,
    loading,
    saving,
    demoMode,

    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,

    drawer,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    fetchGroupes,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}